<template>
  <div class="tab-content">
    <!-- Суммарные метрики -->
    <div class="stats-grid">
      <div class="metric-card metric-card--orders">
        <div class="metric-card__header">
          <span class="metric-card__title">Заказы</span>
          <span class="metric-card__icon">🚚</span>
        </div>
        <span class="metric-card__value">
          {{ totals.ordersCount }}
        </span>
        <span class="metric-card__sub">за выбранный период</span>
      </div>

      <div class="metric-card metric-card--invoices">
        <div class="metric-card__header">
          <span class="metric-card__title">Сумма счетов</span>
          <span class="metric-card__icon">📄</span>
        </div>
        <span class="metric-card__value">
          {{ formatMoney(totals.invoicesTotalAmount) }}
        </span>
        <span class="metric-card__sub">выставлено клиентам</span>
      </div>

      <div class="metric-card metric-card--expenses">
        <div class="metric-card__header">
          <span class="metric-card__title">Расходы</span>
          <span class="metric-card__icon">💸</span>
        </div>
        <span class="metric-card__value">
          {{ formatMoney(totals.expensesAmount) }}
        </span>
        <span class="metric-card__sub">операционные затраты</span>
      </div>

      <div
        class="metric-card metric-card--profit"
        :class="
          totals.profit >= 0
            ? 'metric-card--profit-pos'
            : 'metric-card--profit-neg'
        "
      >
        <div class="metric-card__header">
          <span class="metric-card__title">Прибыль</span>
          <span class="metric-card__icon">
            {{ totals.profit >= 0 ? "📈" : "📉" }}
          </span>
        </div>
        <span class="metric-card__value">
          {{ formatMoney(totals.profit) }}
        </span>
        <span class="metric-card__sub">счета минус расходы</span>
      </div>
    </div>

    <!-- График -->
    <div v-if="hasChartData" class="card chart-card">
      <div class="chart-card__header">
        <h4>Динамика по периодам</h4>
        <div class="filter-tabs">
          <button
            class="filter-tab"
            :class="{ active: effectiveChartType === 'line' }"
            :disabled="effectiveChartType === 'line'"
            @click="chartType = 'line'"
          >
            Линейный
          </button>
          <button
            class="filter-tab"
            :class="{ active: effectiveChartType === 'donut' }"
            :disabled="effectiveChartType === 'donut'"
            @click="chartType = 'donut'"
          >
            Круговой
          </button>
          <button
            v-if="!barTooManyPoints"
            class="filter-tab"
            :class="{ active: effectiveChartType === 'bar' }"
            :disabled="effectiveChartType === 'bar'"
            @click="chartType = 'bar'"
          >
            Столбчатый
          </button>
        </div>
      </div>

      <LineChart
        v-if="effectiveChartType === 'line'"
        :series="chartSeries"
        :points="chartPoints"
        :height="240"
        :format-value="formatChartValue"
      />
      <DonutChart
        v-else-if="effectiveChartType === 'donut'"
        :slices="summaryDonutSlices"
        :format-value="formatMoney"
        center-title="Итого"
      />
      <BarChart
        v-else-if="effectiveChartType === 'bar'"
        :series="chartSeries"
        :points="chartPoints"
        :height="240"
        :format-value="formatChartValue"
      />
    </div>

    <!-- Счета по статусам -->
    <div v-if="hasStatusData" class="card status-card">
      <div class="status-card__header">
        <h4>Счета по статусам</h4>

        <div class="status-card__header-right">
          <div class="status-card__total">
            Всего:
            <strong>{{ totalInvoicesCount }} шт.</strong>
            на
            <strong>
              {{ formatMoney(totals.invoicesTotalAmount) }}
            </strong>
          </div>

          <div class="filter-tabs">
            <button
              class="filter-tab"
              :class="{ active: statusViewType === 'donut' }"
              :disabled="statusViewType === 'donut'"
              @click="statusViewType = 'donut'"
            >
              Диаграмма
            </button>
            <button
              class="filter-tab"
              :class="{ active: statusViewType === 'bars' }"
              :disabled="statusViewType === 'bars'"
              @click="statusViewType = 'bars'"
            >
              Список
            </button>
          </div>
        </div>
      </div>

      <DonutChart
        v-if="statusViewType === 'donut'"
        :slices="invoiceSlices"
        :format-value="formatMoney"
        center-title="Итого"
      />
      <PercentageLineChart
        v-else
        :slices="invoiceSlices"
        :format-value="formatMoney"
      />
    </div>
  </div>
</template>

<script>
import { useOrderInvoiceStore } from "@/stores/orderInvoiceStore";

import LineChart from "@/components/Dashboard/Charts/LineChart.vue";
import BarChart from "@/components/Dashboard/Charts/BarChart.vue";
import DonutChart from "@/components/Dashboard/Charts/DonutChart.vue";
import PercentageLineChart from "@/components/Dashboard/Charts/PercentageLineChart.vue";

import {
  formatMoney,
  formatChartValue,
  formatDisplayDate,
} from "@/utils/dashboardFormatters";

const CHART_TYPE_KEY = "dashboard:chart:chartType";
const VALID_CHART_TYPES = ["line", "bar", "donut"];
const STATUS_VIEW_TYPE_KEY = "dashboard:chart:statusViewType";
const VALID_STATUS_VIEW_TYPES = ["donut", "bars"];
const MAX_BAR_POINTS = 60;

export default {
  name: "DashboardInvoiceTab",

  components: {
    LineChart,
    BarChart,
    DonutChart,
    PercentageLineChart,
  },

  props: {
    periods: {
      type: Array,
      required: true,
    },
  },

  data() {
    const savedChartType = localStorage.getItem(CHART_TYPE_KEY);
    const initialChartType = VALID_CHART_TYPES.includes(
      savedChartType
    )
      ? savedChartType
      : "line";

    const savedStatusViewType = localStorage.getItem(
      STATUS_VIEW_TYPE_KEY
    );
    const initialStatusViewType = VALID_STATUS_VIEW_TYPES.includes(
      savedStatusViewType
    )
      ? savedStatusViewType
      : "donut";

    return {
      invoiceStore: useOrderInvoiceStore(),
      chartType: initialChartType,
      effectiveChartType: initialChartType,
      statusViewType: initialStatusViewType,
      chartSeries: [
        { key: "invoices", label: "Счета", color: "#4c6ef5" },
        { key: "expenses", label: "Расходы", color: "#d64747" },
      ],
    };
  },

  computed: {
    hasChartData() {
      return (
        this.periods.length > 1 &&
        this.chartPoints.some((p) =>
          Object.values(p.values).some((v) => v > 0)
        )
      );
    },

    hasStatusData() {
      return this.invoiceSlices.some((s) => s.value > 0);
    },

    barTooManyPoints() {
      return this.periods.length > MAX_BAR_POINTS;
    },

    totals() {
      const result = this.periods.reduce(
        (acc, period) => {
          acc.ordersCount += period.orders_count || 0;
          acc.invoicesTotalAmount +=
            parseFloat(period.invoices_total_amount) || 0;
          acc.expensesAmount +=
            parseFloat(period.expenses_amount) || 0;

          for (const [status, data] of Object.entries(
            period.invoices_by_status || {}
          )) {
            if (!acc.invoicesByStatus[status]) {
              acc.invoicesByStatus[status] = { count: 0, amount: 0 };
            }
            acc.invoicesByStatus[status].count += data.count || 0;
            acc.invoicesByStatus[status].amount +=
              parseFloat(data.amount) || 0;
          }

          return acc;
        },
        {
          ordersCount: 0,
          invoicesTotalAmount: 0,
          expensesAmount: 0,
          invoicesByStatus: {},
        }
      );
      result.profit =
        result.invoicesTotalAmount - result.expensesAmount;
      return result;
    },

    chartPoints() {
      return this.periods.map((p) => ({
        label: formatDisplayDate(p.period_start, p.period_end),
        values: {
          invoices: parseFloat(p.invoices_total_amount) || 0,
          expenses: parseFloat(p.expenses_amount) || 0,
        },
      }));
    },

    summaryDonutSlices() {
      return [
        {
          key: "invoices",
          label: "Счета",
          color: "#4c6ef5",
          value: this.totals.invoicesTotalAmount,
          count: null,
        },
        {
          key: "expenses",
          label: "Расходы",
          color: "#d64747",
          value: this.totals.expensesAmount,
          count: null,
        },
      ];
    },

    invoiceStatuses() {
      return Object.entries(this.totals.invoicesByStatus).map(
        ([key, data]) => ({
          key,
          label: this.invoiceStore.formatInvoiceStatus(key),
          color: this.invoiceStore.invoiceStatusColor(key),
          count: data.count,
          amount: data.amount,
        })
      );
    },

    totalInvoicesCount() {
      return this.invoiceStatuses.reduce(
        (sum, s) => sum + s.count,
        0
      );
    },

    invoiceSlices() {
      return this.invoiceStatuses.map((s) => ({
        key: s.key,
        label: s.label,
        color: s.color,
        value: s.amount,
        count: s.count,
      }));
    },
  },

  watch: {
    chartType(value) {
      localStorage.setItem(CHART_TYPE_KEY, value);
      if (value === "bar" && this.barTooManyPoints) {
        this.effectiveChartType = "line";
      } else {
        this.effectiveChartType = value;
      }
    },

    barTooManyPoints(tooMany) {
      if (tooMany && this.chartType === "bar") {
        this.effectiveChartType = "line";
      } else {
        this.effectiveChartType = this.chartType;
      }
    },

    statusViewType(value) {
      localStorage.setItem(STATUS_VIEW_TYPE_KEY, value);
    },
  },

  created() {
    if (this.chartType === "bar" && this.barTooManyPoints) {
      this.effectiveChartType = "line";
    }
  },

  methods: {
    formatMoney,
    formatChartValue,
  },
};
</script>

<style scoped>
.tab-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: fadeIn 0.25s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ── Метрики ── */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.metric-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: var(--base-in-indent);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-top: 3px solid transparent;
  position: relative;
  overflow: hidden;
}

.metric-card::before {
  content: "";
  position: absolute;
  inset: 0;
  opacity: 0.04;
  pointer-events: none;
}

.metric-card--orders {
  border-top-color: #00bfa5;
}
.metric-card--orders::before {
  background: #00bfa5;
}
.metric-card--invoices {
  border-top-color: #4c6ef5;
}
.metric-card--invoices::before {
  background: #4c6ef5;
}
.metric-card--expenses {
  border-top-color: #d64747;
}
.metric-card--expenses::before {
  background: #d64747;
}
.metric-card--profit-pos {
  border-top-color: #00bfa5;
}
.metric-card--profit-pos::before {
  background: #00bfa5;
}
.metric-card--profit-neg {
  border-top-color: #d64747;
}
.metric-card--profit-neg::before {
  background: #d64747;
}

.metric-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.metric-card__title {
  font-size: 13px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-card__icon {
  font-size: 22px;
  line-height: 1;
}

.metric-card__value {
  font-size: 28px;
  font-weight: 700;
  color: var(--color-dark);
  line-height: 1;
  letter-spacing: -0.5px;
}

.metric-card--profit-neg .metric-card__value {
  color: var(--color-danger);
}
.metric-card--profit-pos .metric-card__value {
  color: var(--color-green);
}

.metric-card__sub {
  font-size: 12px;
  color: #bbb;
  margin-top: auto;
}

/* ── График ── */
.chart-card {
  gap: 16px;
}

.chart-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

/* ── Общие табы ── */
.filter-tabs {
  display: flex;
  gap: 4px;
  background: var(--color-grey);
  padding: 3px;
  border-radius: 8px;
  flex-shrink: 0;
}

.filter-tab {
  padding: 7px 16px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  color: #888;
  font-family: "Roboto", sans-serif;
  font-weight: 500;
}

.filter-tab.active {
  background: #fff;
  color: var(--color-dark);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}

.filter-tab:disabled {
  cursor: default;
  pointer-events: none;
}

/* ── Карточка статусов ── */
.status-card {
  gap: 20px;
}

.status-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.status-card__header-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.status-card__total {
  font-size: 13px;
  color: #999;
  white-space: nowrap;
}

.status-card__total strong {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-dark);
}

/* ── Адаптив ── */
@media screen and (max-width: 1100px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media screen and (max-width: 767px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .metric-card__value {
    font-size: 22px;
  }

  .chart-card__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .status-card__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .status-card__header-right {
    width: 100%;
    justify-content: space-between;
  }
}

@media screen and (max-width: 480px) {
  .stats-grid {
    gap: 10px;
  }

  .metric-card {
    padding: 14px;
    gap: 8px;
  }
  .metric-card__value {
    font-size: 19px;
  }
  .metric-card__icon {
    font-size: 18px;
  }
}
</style>
